import { useFlags } from "launchdarkly-react-client-sdk";
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { Socket, io } from "socket.io-client";

import { WEBSOCKET_API_URL } from "@/constants";
import { useLatestValueRef } from "@/hooks/useLatestValueRef";
import { getAuthErrorCode } from "@/monitoring/benign-auth-errors";
import { getCurrentAccountIdScope } from "@/providers/account";
import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";

import { ClientToServerEvents } from "./ClientToServerEvents";
import { ServerToClientEvents } from "./ServerToClientEvents";

type SocketInstance = Socket<ServerToClientEvents, ClientToServerEvents>;

const SocketContext = createContext<SocketInstance | null>(null);

export function SocketProvider(props: { children: ReactNode }) {
  const { getAccessTokenSilently } = useAuth0();
  const [, setIsConnected] = useState(false);

  const { testWebsocketsLongPolling } = useFlags();

  const [socket] = useState<SocketInstance>(() => {
    return io(WEBSOCKET_API_URL, {
      autoConnect: true,
      transports: testWebsocketsLongPolling ? ["polling"] : undefined,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: import.meta.env.DEV ? 20 : Infinity,
      auth: async (cb) => {
        try {
          const token = await getAccessTokenSilently();
          cb({
            jwt: `Bearer ${token}`,
            // Read per connection attempt, not captured: this callback is
            // created once but runs again on every reconnect.
            "x-account-id": getCurrentAccountIdScope(),
          });
        } catch (e) {
          // A session that merely needs renewing is grouped into one
          // warning-level issue by Sentry's beforeSend, which matters most here:
          // socket.io reconnects indefinitely, so one stale session retries this
          // forever.
          Sentry.captureException(e, {
            extra: {
              code: getAuthErrorCode(e),
              description: (e as { error_description?: string })
                ?.error_description,
            },
          });
          cb({});
        }
      },
    });
  });

  // Visibility & Lifecycle Management
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        if (socket.disconnected) {
          socket.connect();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [socket]);

  useEffect(() => {
    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function onConnect() {
      setIsConnected(true);
      socket.emit("join:workspace");
    }

    function onDisconnect() {
      setIsConnected(false);
    }

    function onAllEvents(event: string, payload: any) {
      /* eslint-disable no-console */
      console.groupCollapsed(`%c[socket event]: ${event}`, "color: blue;");
      console.log(payload);
      console.groupEnd();
      /* eslint-enable no-console */
    }

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);

    if (import.meta.env.DEV) {
      socket.onAny(onAllEvents);
    }

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.offAny(onAllEvents);
    };
  }, [socket]);

  return (
    <SocketContext.Provider value={socket}>
      {props.children}
    </SocketContext.Provider>
  );
}

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocketContext must be used within SocketProvider");
  }
  return context;
};

export function useSocketEvent<TName extends keyof ServerToClientEvents>(
  eventName: TName,
  options: {
    onMessage?: ServerToClientEvents[TName];
  } = {},
) {
  const socket = useSocket();
  const onMessageRef = useLatestValueRef(options.onMessage);
  const [lastMessage, setLastMessage] =
    useState<Parameters<ServerToClientEvents[TName]>[0]>();

  useEffect(() => {
    function onEvent(resp: any) {
      onMessageRef.current?.(resp);
      setLastMessage(resp);
    }

    socket.on(eventName, onEvent as any);
    return () => {
      socket.off(eventName, onEvent as any);
    };
  }, [eventName, socket, onMessageRef]);

  return {
    lastMessage,
  };
}
